import { Section, CodeBlock, InfoBox } from "@/components"
import { Terminal, Play, Wrench } from "lucide-react"

export function MongoDBScriptsPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Scripts & Commands</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Explore the available npm scripts and helper commands to streamline your development workflow.
        </p>
      </div>

      <Section title="Development Scripts" description="Commands for everyday development">
        <div className="grid md:grid-cols-2 gap-6">
          <ScriptCard
            icon={<Play className="h-5 w-5 text-primary" />}
            command="npm run start:dev"
            description="Start the application in watch mode with hot reload"
          />
          <ScriptCard
            icon={<Play className="h-5 w-5 text-primary" />}
            command="npm run start"
            description="Start the application normally (no watch mode)"
          />
          <ScriptCard
            icon={<Terminal className="h-5 w-5 text-primary" />}
            command="npm run build"
            description="Build the application for production"
          />
          <ScriptCard
            icon={<Terminal className="h-5 w-5 text-primary" />}
            command="npm run start:prod"
            description="Run the production build"
          />
        </div>
      </Section>

      <Section title="Setup Script" description="Interactive project setup">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template includes an interactive setup script that helps you configure 
            your project quickly.
          </p>

          <CodeBlock
            code={`npm run setup`}
            language="bash"
          />

          <div className="bg-muted/30 rounded-lg p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Wrench className="h-5 w-5 text-primary" />
              What the setup script does:
            </h3>
            <ol className="space-y-3 list-decimal list-inside text-sm text-muted-foreground ml-4">
              <li>Checks for Node.js installation</li>
              <li>Installs npm dependencies</li>
              <li>Prompts for MongoDB URI (or uses default)</li>
              <li>Generates secure JWT secrets automatically</li>
              <li>Asks if you want to configure Google OAuth</li>
              <li>Creates a .env file with all settings</li>
              <li>Validates the configuration</li>
              <li>Shows next steps</li>
            </ol>
          </div>

          <InfoBox type="success" title="Recommended">
            Always use the setup script for new installations. It ensures all 
            required configuration is properly set up.
          </InfoBox>
        </div>
      </Section>

      <Section title="Code Quality Scripts" description="Linting and formatting">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">ESLint</h3>
            <CodeBlock
              code={`# Check for linting errors
npm run lint

# Fix auto-fixable issues
npm run lint:fix`}
              language="bash"
            />
            <p className="text-sm text-muted-foreground">
              ESLint is configured with NestJS recommended rules and TypeScript support.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Prettier</h3>
            <CodeBlock
              code={`# Format code
npm run format

# Check formatting
npm run format:check`}
              language="bash"
            />
            <p className="text-sm text-muted-foreground">
              Prettier ensures consistent code formatting across the project.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Testing Scripts" description="Run tests and check coverage">
        <div className="space-y-6">
          <InfoBox type="info" title="Tests Coming Soon">
            Testing infrastructure is set up but test files need to be implemented.
          </InfoBox>

          <div className="grid md:grid-cols-2 gap-6">
            <ScriptCard
              icon={<Terminal className="h-5 w-5 text-primary" />}
              command="npm run test"
              description="Run unit tests"
            />
            <ScriptCard
              icon={<Terminal className="h-5 w-5 text-primary" />}
              command="npm run test:watch"
              description="Run tests in watch mode"
            />
            <ScriptCard
              icon={<Terminal className="h-5 w-5 text-primary" />}
              command="npm run test:cov"
              description="Run tests with coverage report"
            />
            <ScriptCard
              icon={<Terminal className="h-5 w-5 text-primary" />}
              command="npm run test:e2e"
              description="Run end-to-end tests"
            />
          </div>
        </div>
      </Section>

      <Section title="Utility Commands" description="Helpful commands for development">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Generate JWT Secrets</h3>
            <CodeBlock
              code={`node -e "console.log(require('crypto').randomBytes(64).toString('hex'))"`}
              language="bash"
            />
            <p className="text-sm text-muted-foreground">
              Generate secure random strings for JWT_SECRET and JWT_REFRESH_SECRET.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Start MongoDB Locally</h3>
            <CodeBlock
              code={`# Using Docker
docker run -d -p 27017:27017 --name mongodb mongo:latest

# Or install MongoDB Community Edition
# See: https://docs.mongodb.com/manual/installation/`}
              language="bash"
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Check MongoDB Connection</h3>
            <CodeBlock
              code={`# Using MongoDB Shell
mongosh mongodb://localhost:27017

# Or using MongoDB Compass (GUI)
# Connect to: mongodb://localhost:27017`}
              language="bash"
            />
          </div>
        </div>
      </Section>

      <Section title="Package.json Scripts Reference" description="Complete list of available scripts">
        <CodeBlock
          code={`{
  "scripts": {
    // Development
    "start": "nest start",
    "start:dev": "nest start --watch",
    "start:debug": "nest start --debug --watch",
    "start:prod": "node dist/main",
    
    // Build
    "build": "nest build",
    
    // Code Quality
    "lint": "eslint \\"{src,apps,libs,test}/**/*.ts\\" --fix",
    "format": "prettier --write \\"src/**/*.ts\\" \\"test/**/*.ts\\"",
    
    // Testing
    "test": "jest",
    "test:watch": "jest --watch",
    "test:cov": "jest --coverage",
    "test:debug": "node --inspect-brk -r tsconfig-paths/register -r ts-node/register node_modules/.bin/jest --runInBand",
    "test:e2e": "jest --config ./test/jest-e2e.json",
    
    // Setup
    "setup": "node scripts/setup.js"
  }
}`}
          language="json"
        />
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Pro Tips</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use <code className="bg-muted px-2 py-1 rounded">npm run start:dev</code> during development for hot reload</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Run <code className="bg-muted px-2 py-1 rounded">npm run lint:fix</code> before committing code</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Always test your builds with <code className="bg-muted px-2 py-1 rounded">npm run build</code> before deployment</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use environment variables for different environments (development, staging, production)</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

function ScriptCard({ 
  icon, 
  command, 
  description 
}: { 
  icon: React.ReactNode
  command: string
  description: string
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-3">
      <div className="flex items-start gap-3">
        <div className="mt-1">{icon}</div>
        <div className="flex-1 space-y-2">
          <code className="bg-muted px-3 py-1 rounded text-sm font-mono block">
            {command}
          </code>
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  )
}

